import { StringDevUIField } from "./StringDevUIField";

const HOLD_DELAY_MS = 320;
const HOLD_INTERVAL_MS = 60;
const DRAG_PIXELS_PER_STEP = 6;

export class StringDevFieldNumber extends StringDevUIField<number> {
  private min: number;
  private max: number;
  private step: number;
  private input: HTMLInputElement | null = null;

  constructor(
    key: string,
    label: string,
    defaultValue: number,
    onChange: (key: string, value: number) => void,
    min: number = 0,
    max: number = 100,
    step: number = 1,
  ) {
    super(key, label, defaultValue, onChange);
    this.min = min;
    this.max = max;
    this.step = step;
  }

  build(): HTMLElement {
    const row = this.createRow();
    row.appendChild(this.createLabel());
    row.appendChild(this.createControls());
    this.root = row;
    return row;
  }

  setValue(value: number): void {
    this.value = value;
    if (this.input) this.input.value = this.formatValue(value);
  }

  private createControls(): HTMLDivElement {
    const control = document.createElement("div");
    control.setAttribute("data-string-dev-number-input", "");

    const valueWrap = document.createElement("div");
    valueWrap.setAttribute("data-string-dev-input-value-wrap", "");
    valueWrap.appendChild(this.createInput());

    const stepper = document.createElement("div");
    stepper.setAttribute("data-string-dev-stepper-stack", "");
    stepper.appendChild(this.createStepperButton("up", 1));
    stepper.appendChild(this.createStepperButton("down", -1));

    control.appendChild(valueWrap);
    control.appendChild(stepper);
    return control;
  }

  private createInput(): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "text";
    input.inputMode = "decimal";
    input.value = this.formatValue(this.value);
    input.setAttribute("data-string-dev-field-input", "");

    input.addEventListener("focus", () => {
      input.select();
    });
    input.addEventListener("blur", () => {
      this.commitValue(input.value);
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        input.blur();
        return;
      }
      if (event.key === "Escape") {
        input.value = this.formatValue(this.value);
        input.blur();
        return;
      }
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      this.applyDelta(event.key === "ArrowUp" ? 1 : -1, event);
    });
    input.addEventListener("wheel", (event) => {
      if (document.activeElement !== input) return;
      event.preventDefault();
      this.applyDelta(event.deltaY < 0 ? 1 : -1, event);
    });
    input.addEventListener("mousedown", (event) => {
      if (event.button !== 1) return;
      this.startMiddleDrag(event, input);
    });
    input.addEventListener("auxclick", (event) => {
      if (event.button === 1) event.preventDefault();
    });

    this.input = input;
    return input;
  }

  private createStepperButton(
    directionName: "up" | "down",
    direction: 1 | -1,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-string-dev-stepper-btn", "");
    button.setAttribute("data-direction", directionName);

    const icon = document.createElement("span");
    icon.setAttribute("data-string-dev-stepper-icon", "");
    icon.innerHTML =
      directionName === "up"
        ? `<svg viewBox="0 0 12 12" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 7L6 5L9 7" fill="currentColor"/><path d="M3 7L6 5L9 7" fill="none" stroke="currentColor"/></svg>`
        : `<svg viewBox="0 0 12 12" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5L6 7L9 5" fill="currentColor"/><path d="M3 5L6 7L9 5" fill="none" stroke="currentColor"/></svg>`;
    button.appendChild(icon);

    let holdTimeout = 0;
    let holdInterval = 0;
    let suppressClick = false;

    const stopHold = () => {
      window.clearTimeout(holdTimeout);
      window.clearInterval(holdInterval);
      document.removeEventListener("pointerup", stopHold);
      document.removeEventListener("pointercancel", stopHold);
    };

    button.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;

      holdTimeout = window.setTimeout(() => {
        suppressClick = true;
        this.applyDelta(direction);
        holdInterval = window.setInterval(() => {
          this.applyDelta(direction);
        }, HOLD_INTERVAL_MS);
      }, HOLD_DELAY_MS);

      document.addEventListener("pointerup", stopHold);
      document.addEventListener("pointercancel", stopHold);
    });

    button.addEventListener("click", (event) => {
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        return;
      }

      this.applyDelta(direction);
      this.input?.focus();
      this.input?.select();
    });

    return button;
  }

  private startMiddleDrag(
    event: MouseEvent,
    input: HTMLInputElement,
  ): void {
    event.preventDefault();

    let previousX = event.clientX;
    let accumulated = 0;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";

    const stopDrag = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", stopDrag);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - previousX;
      previousX = moveEvent.clientX;
      accumulated += deltaX;

      while (Math.abs(accumulated) >= DRAG_PIXELS_PER_STEP) {
        const direction = accumulated > 0 ? 1 : -1;
        this.applyDelta(direction, moveEvent);
        accumulated -= DRAG_PIXELS_PER_STEP * direction;
      }
    };

    input.focus();
    input.select();
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", stopDrag);
  }

  private commitValue(rawValue: string): void {
    const parsed = parseFloat(rawValue.replace(",", "."));
    if (isNaN(parsed)) {
      if (this.input) this.input.value = this.formatValue(this.value);
      return;
    }

    const nextValue = this.normalizeValue(parsed);
    if (this.input) this.input.value = this.formatValue(nextValue);
    this.emit(nextValue);
  }

  private applyDelta(
    direction: 1 | -1,
    event?: KeyboardEvent | WheelEvent | MouseEvent,
  ): void {
    const multiplier = event?.shiftKey ? 10 : event?.altKey ? 0.1 : 1;
    const nextValue = this.normalizeValue(
      this.value + direction * this.step * multiplier,
    );
    if (this.input) this.input.value = this.formatValue(nextValue);
    this.emit(nextValue);
  }

  private normalizeValue(value: number): number {
    const snapped = Math.round(value / this.step) * this.step;
    const precision = this.getPrecision(this.step);
    return Number(snapped.toFixed(precision));
  }

  private getPrecision(value: number): number {
    const parts = String(value).split(".");
    return parts[1]?.length ?? 0;
  }

  private formatValue(value: number): string {
    const precision = this.getPrecision(this.step);
    return precision > 0 ? value.toFixed(precision) : String(value);
  }
}
